import React from 'react';
import { ApplicationState } from '../../store';
import { Dispatch } from 'redux';
import { Helmet } from 'react-helmet';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import CssBaseline from '@material-ui/core/CssBaseline';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Link from '@material-ui/core/Link';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import { onLogin } from '../../store/UserSession/UserSessionActions';
import { AuthenticateRequest } from '../../Interfaces/AuthenticationRequest';
import signBgrImage from '../../assets/images/signBgrImage.png';
import { APP_TITLE } from '../../utils/constants';
import StringResourceManager, {
  GetString,
} from '../../components/StringResourceManager';
import { useState, useEffect } from 'react';
import { getMsalInstance } from '../../components/SSO/msalInstance';
import { PublicClientApplication } from '@azure/msal-browser';
import { useAuth0 } from '@auth0/auth0-react';
import { IdentityManagerType } from '../../Interfaces/SSO/IdentityManagerConfiguration';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { InputAdornment, IconButton } from '@mui/material';

// Styles
const useStyles = makeStyles(theme => ({
  root: {
    height: '100vh',
  },
  image: {
    backgroundImage: `url(${signBgrImage})`,
    backgroundRepeat: 'no-repeat',
    backgroundColor: '#3d5afe',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  paper: {
    margin: theme.spacing(8, 4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  errorMessage: {
    color: '#FF0000',
  },
  altMessage: {
    color: '#4051B5',
    fontSize: 18,
  },
}));

function SignIn() {
  const classes = useStyles();
  const history = useHistory();

  const identityMgrType: IdentityManagerType = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.identityMgrType,
  );

  const dispatch: Dispatch<any> = useDispatch();

  const isLoggedIn: boolean = useSelector(
    (state: ApplicationState) => state.userSessionReducer.isLoggedIn,
  );

  useEffect((): ReturnType<React.EffectCallback> => {
    if (isLoggedIn) {
      history.push('/Overview');
    }
    return (): void => {};
  }, [isLoggedIn]);

  const BasePage = (props: any) => {
    return (
      <>
        <Helmet>
          <title>
            {GetString('PAGE_TITLE_SIGNIN')} | {APP_TITLE}
          </title>
        </Helmet>
        <Grid container component="main" className={classes.root}>
          <CssBaseline />
          <Grid item xs={false} sm={4} md={7} className={classes.image} />
          <Grid
            item
            xs={12}
            sm={8}
            md={5}
            component={Paper}
            elevation={6}
            square
          >
            <div className={classes.paper}>
              <StringResourceManager
                component="h1"
                variant="h5"
                stringKey="PAGE_TITLE_SIGNIN"
              />
              <LoginForm identityMgrType={identityMgrType} />
            </div>
          </Grid>
        </Grid>
      </>
    );
  };

  const PlainLoginForm: any = () => {
    const [userName, setUserName] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const loginFailed: boolean = useSelector(
      (state: ApplicationState) => state.userSessionReducer.loginFailed,
    );

    const loginFailedMessage: string = useSelector(
      (state: ApplicationState) => state.userSessionReducer.loginFailedMessage,
    );

    const handleUserNameTextFieldChanged = (e: any) => {
      setUserName(e.target.value);
    };

    const handlePasswordTextFieldChanged = (e: any) => {
      setPassword(e.target.value);
    };

    const handleSubmit = async (event: any) => {
      event.preventDefault();
      authenticate();
    };

    const authenticate = async () => {
      let credentials: AuthenticateRequest = {
        Username: userName,
        Password: password,
      };

      onLogin(dispatch, credentials, undefined);
    };

    useEffect((): ReturnType<React.EffectCallback> => {
      if (loginFailed) {
        setPassword('');
      }
      return (): void => {};
    }, [loginFailed]);

    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => {
      setShowPassword(prev => !prev);
    };

    return (
      <>
        <form className={classes.form} noValidate onSubmit={handleSubmit}>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="userName"
            label={GetString('USERNAME_PLACEHOLDER')}
            id="userName"
            autoComplete="email"
            autoFocus
            onChange={handleUserNameTextFieldChanged}
            value={userName}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="password"
            label={GetString('PASSWORD_PLACEHOLDER')}
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            onChange={handlePasswordTextFieldChanged}
            value={password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility} edge="end">
                    {showPassword ? (
                      <VisibilityOffIcon />
                    ) : (
                      <RemoveRedEyeIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {loginFailed ? (
            <StringResourceManager
              component="h1"
              style="errorMessage"
              stringKey={loginFailedMessage}
            />
          ) : null}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
          >
            {GetString('PAGE_TITLE_SIGNIN')}
          </Button>
        </form>
      </>
    );
  };

  const MsalLoginForm: any = () => {
    const [loading, setLoading] = useState<boolean>(false);

    const handleMsalLogin = async (event: any) => {
      setLoading(true);
      event.preventDefault();
      await authenticateMsal();
    };

    const authenticateMsal = async () => {
      const msalInstance = getMsalInstance() as PublicClientApplication;
      let popupRequest = {
        scopes: [
          msalInstance.getConfiguration().auth.clientId +
            '/Msal.Access.User.Read',
        ],
        authority: msalInstance.getConfiguration().auth.authority,
        prompt: 'login',
      };
      await msalInstance
        .loginPopup(popupRequest)
        .then((response: { idToken: any } | null) => {
          if (response !== null) {
            // Acquire token silent success
            let idToken = response.idToken;
            // Call your API with token
            onLogin(dispatch, undefined, idToken);
          } else setLoading(false);
        })
        .catch((error: any) => {
          setLoading(false);
          console.log(error);
        });
    };

    return (
      <>
        <form className={classes.form} noValidate onSubmit={handleMsalLogin}>
          <Grid className={classes.altMessage}>
            {GetString('SSO_SIGNIN_MESSAGE')}
          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            disabled={loading}
          >
            {GetString('SSO_SIGNIN_BUTTON')}
          </Button>
        </form>
      </>
    );
  };

  const Auth0LoginForm: any = () => {
    const { isLoading, isAuthenticated, loginWithPopup, getIdTokenClaims } =
      useAuth0();

    useEffect((): ReturnType<React.EffectCallback> => {
      if (isLoading) {
        return;
      }

      if (isAuthenticated && !isLoggedIn) {
        getIdTokenClaims().then((token: any) => {
          if (token) {
            onLogin(dispatch, undefined, token?.__raw);
          }
        });
      }
      return (): void => {};
    }, [isLoading, isAuthenticated]);

    const handleAuth0Login = async () => {
      const login = await loginWithPopup();
    };

    return (
      <>
        <form className={classes.form} noValidate>
          <Grid className={classes.altMessage}>
            {GetString('SSO_SIGNIN_MESSAGE')}
          </Grid>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            onClick={handleAuth0Login}
            disabled={isLoading || isAuthenticated}
          >
            {GetString('SSO_SIGNIN_BUTTON')}
          </Button>
        </form>
      </>
    );
  };

  function LoginForm(props: { identityMgrType: IdentityManagerType }) {
    if (props.identityMgrType === IdentityManagerType.Msal) {
      return <MsalLoginForm />;
    } else if (props.identityMgrType === IdentityManagerType.Auth0) {
      return <Auth0LoginForm />;
    } else if (props.identityMgrType === IdentityManagerType.ActiveDirectory) {
      return <PlainLoginForm />;
    } else {
      return <PlainLoginForm />;
    }
  }

  return <BasePage identityMgrType={identityMgrType} />;
}
export default connect()(SignIn);
